// Utility to build a variable map from current nodes & edges
// VariableRecord = { name, definedAt: string[], usedAt: string[], actualValue, status }
export function buildVariableMap(nodes, edges) {
  const map = new Map();
  const definedVars = new Set(); // exact variable names defined by input nodes

  function getOrCreate(name) {
    if (!map.has(name)) {
      map.set(name, { name, definedAt: [], usedAt: [], actualValue: undefined, status: 'ok' });
    }
    return map.get(name);
  }

  function escapeReg(s = '') { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  // Node types considered user-input (definitions)
  // include 'question' type to capture standard question nodes
  // Node types regarded as genuine input collectors (definitions)
  // Email nodes merely use the Submitter_Email variable, so they are excluded here
  const inputTypes = ['text','number','choice','yes_no','scale','question'];

  // 1. Definitions & inline uses
  nodes.forEach((n) => {
    const varName = n.data?.variable || n.data?.variable_name;
    const label = n.data?.id || n.label || n.id;

    // Package nodes define their collected variables under effective (namespaced)
    // names `${instanceName}__${v}` — the names host authors reference in
    // downstream conditions and see in the property panel (C3, WP-2 item 6).
    if (n.type === 'package') {
      const inst = n.data?.instanceName || n.id;
      const pkgVars = Array.isArray(n.data?.variables) ? n.data.variables : [];
      pkgVars.forEach((v) => {
        if (!v) return;
        const eff = `${inst}__${v}`;
        const rec = getOrCreate(eff);
        if (!rec.definedAt.includes(inst)) rec.definedAt.push(inst);
        definedVars.add(eff);
      });
      return; // fully handled — no single self variable to record
    }

    // File question type produces File_Upload_Path
    if (n.data?.type === 'file') {
      const recFU = getOrCreate('File_Upload_Path');
      if (!recFU.definedAt.includes(label)) recFU.definedAt.push(label);
    }

    // Special rule: any node with question-type 'email' is a consumer
    if (n.data?.type === 'email') {
      const rec = getOrCreate('Submitter_Email');
      if (!rec.usedAt.includes(label)) rec.usedAt.push(label);
      return; // do not treat as definition
    }

    if (!varName) return;

    // Resolve collectsInput with root-first fallback to legacy data.collectsInput
    const collects = (n.collectsInput !== undefined)
      ? n.collectsInput !== false
      : (n.data?.collectsInput !== undefined)
        ? n.data.collectsInput !== false
        : true;

    // Only treat as a definition when node actually collects input
    if (collects && inputTypes.includes(n.type)) {
      const rec = getOrCreate(varName);
      rec.definedAt.push(label);
      definedVars.add(varName);
    } else {
      // Do not count the node's own variable as a "use" when it does not collect input
      // Legacy nodes may retain variable_name but should not appear as noise
      // So we skip adding a usedAt entry here for the node's self.
    }
  });

  // 2. Uses in edge conditions: match only against actually-defined variable names
  edges.forEach((e) => {
    if (!e?.data?.condition) return;

    // If source node no longer exists, do not record this use
    const sourceNode = nodes.find(n => n.id === e.source);
    if (!sourceNode) return;

    // Strip string literals so quoted values aren’t treated as variables
    const stripped = e.data.condition.replace(/(['"][^'"\\]*(?:\\.[^'"\\]*)*['"])/g, '');

    const sourceLabel = sourceNode?.data?.id || e.source;
    const targetNode = nodes.find(n => n.id === e.target);
    const usedLabel = targetNode ? (targetNode.data?.id || e.target) : `${sourceLabel}→(missing)`;

    // word-like boundaries around the exact variable name; allow hyphens/digits inside names
    definedVars.forEach((name) => {
      if (!name) return;
      const re = new RegExp(`(^|[^A-Za-z0-9_-])${escapeReg(name)}(?=$|[^A-Za-z0-9_-])`);
      if (re.test(stripped)) {
        const rec = getOrCreate(name);
        if (!rec.usedAt.includes(usedLabel)) rec.usedAt.push(usedLabel);
      }
    });
  });

  // 3. Uses declared explicitly on a node (requiredVariables array)
  nodes.forEach((n) => {
    if (Array.isArray(n.data?.requiredVariables)) {
      const label = n.data?.id || n.id;
      n.data.requiredVariables.forEach((v) => {
        const rec = getOrCreate(v);
        if (!rec.usedAt.includes(label)) rec.usedAt.push(label);
      });
    }

    // Ensure upload_link nodes always consume Submitter_Email
    if (n.type === 'upload_link') {
      const label = n.data?.id || n.id;
      const rec = getOrCreate('Submitter_Email');
      if (!rec.usedAt.includes(label)) rec.usedAt.push(label);
    }
  });

  // 4. derive status
  map.forEach((rec) => {
    if (rec.definedAt.length === 0 && rec.usedAt.length > 0) rec.status = 'undefined';
    else if (rec.definedAt.length > 1) rec.status = 'duplicate';
    else if (rec.definedAt.length === 1 && rec.usedAt.length === 0) rec.status = 'unused';
    else rec.status = 'ok';
  });

  return map;
}
